import { useMemo } from 'react';
import { Maybe } from 'types';

/**
 * Helper method to filter falsey items from an array.
 * @param item Item to filter.
 * @example
 * const filtered = items.filter(assertTruthy);
 */
export function assertTruthy<T>(item: T | false | undefined | null): item is T {
  return !!item;
}

export function assertPresent<T>(item: T | undefined | null): item is T {
  return item !== null && item !== undefined;
}

export function getValidItems<TItem>(items: Maybe<TItem>[], transform?: never): TItem[];
export function getValidItems<TItem, TReturn>(
  items: Maybe<TItem>[],
  transform: (item: TItem) => TReturn
): NonNullable<TReturn>[];
export function getValidItems<TItem, TReturn>(
  items: Maybe<TItem>[],
  transform?: (item: TItem) => TReturn
): TItem[] | TReturn[] {
  const validItems: (TItem | TReturn)[] = [];
  items.filter(assertPresent).forEach((item) => {
    const outputItem = transform ? transform(item) : item;
    if (assertPresent(outputItem)) {
      validItems.push(outputItem);
    }
  });
  return validItems as TItem[] | TReturn[];
}

type ConnectionEdges<TNode> = { edges?: { node?: TNode | null }[] | null };
const EMPTY: any[] = [];
export const getNodes = <TNode>(data?: ConnectionEdges<TNode>) => {
  const { edges } = data || {};
  if (!edges) {
    return EMPTY as TNode[];
  }

  return getValidItems(edges, ({ node }) => node);
};

/** memoized getNodes */
export const useGetNodes = <TNode>(data?: ConnectionEdges<TNode>) => {
  return useMemo(() => getNodes(data), [data]);
};
